// Couleurs de l'agenda : celle du cabinet, remplacée par celle du collaborateur s'il en a choisi une.
// Fonctions pures, sans React ni Supabase.

export interface ColoredType {
  id: string;
  color: string | null;
}

export interface ColorOverride {
  event_type_id: string | null;
  task_template_id: string | null;
  color: string;
}

/** Couleur par identifiant de type d'événement ou de tâche type. */
export type ColorMap = Record<string, string>;

export const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Palette proposée dans les réglages. Une couleur hors palette reste acceptée. */
export const EVENT_COLOR_PALETTE = [
  '#2563eb',
  '#0ea5e9',
  '#0d9488',
  '#16a34a',
  '#eab308',
  '#f97316',
  '#dc2626',
  '#db2777',
  '#9333ea',
  '#64748b',
] as const;

export function buildColorMap(
  eventTypes: ColoredType[],
  taskTemplates: ColoredType[],
  overrides: ColorOverride[],
): ColorMap {
  const map: ColorMap = {};
  for (const t of [...eventTypes, ...taskTemplates]) {
    if (t.color && HEX_COLOR.test(t.color)) map[t.id] = t.color;
  }
  for (const o of overrides) {
    const id = o.event_type_id ?? o.task_template_id;
    if (id && HEX_COLOR.test(o.color)) map[id] = o.color;
  }
  return map;
}

/** Fond clair d'un bloc d'agenda : la couleur du type, très diluée dans le blanc. */
export function tintOf(color: string): string {
  return `color-mix(in srgb, ${color} 20%, white)`;
}

export type AgendaColorMode = 'type' | 'dossier';

export type PaintedKind = 'hearing' | 'task' | 'prescription' | 'appointment';

/** Couleur d'un événement sans type : les tons sobres de la charte, loin des couleurs vives des types. */
export const UNTYPED_COLORS: Record<PaintedKind, string> = {
  hearing: '#b08968',
  appointment: '#3d5a66',
  task: '#043429',
  prescription: '#444444',
};

/**
 * Couleur d'un bloc en mode « par type » : celle de son type, sinon le ton sobre de sa famille.
 * null en mode « par dossier » : l'agenda garde alors sa teinte par dossier.
 */
export function eventTypeColor(
  event: { kind: PaintedKind; typeId?: string | null },
  mode: AgendaColorMode,
  colors: ColorMap,
): string | null {
  if (mode === 'dossier') return null;
  if (event.kind === 'prescription') return UNTYPED_COLORS.prescription;
  return (event.typeId && colors[event.typeId]) || UNTYPED_COLORS[event.kind];
}
